import { ChevronDown, TextScale } from '@carbon/icons-react'
import { useMediaQuery } from '@mantine/hooks'
import SelectPrimitive, {
  SelectItem as SelectItemPrimitve,
  type TriggerProps,
} from 'app/_components/Select'
import { Tooltip } from 'app/_components/Tooltip'
import { cx } from 'lib/cx'
import { ReactEditor, useSlate } from 'slate-react'
import { selectedBlockTypes, toggleBlock } from '../extensions/formatting'
import { isInTable } from '../extensions/withTables'
import { type Block } from '.'
import { LABEL_MAP } from './labels'

const styleMap = {
  'heading-one': 'group-[.viewport]:text-[28px]/[2.25rem] font-light',
  'heading-two': 'group-[.viewport]:text-[20px]/[1.75rem]',
  'heading-three': 'group-[.viewport]:text-[16px]/[1.5rem]',
} as Record<Block, string>

export function Select({ children }: { children: React.ReactNode }) {
  const editor = useSlate()
  const selected = selectedBlockTypes(editor)
  const isCursorInTable = isInTable(editor)
  const selectedTypesCount = selected
    ? new Set(selected.filter(Boolean)).size
    : 0

  function handleClose(e: Event) {
    e.preventDefault()
    ReactEditor.focus(editor)
  }

  return (
    <Tooltip content="Absatzformat" delayDuration={200}>
      <div>
        <SelectPrimitive
          disabled={isCursorInTable}
          label="Formatierung"
          placeholder={
            selectedTypesCount > 1 ? 'Diverse Stile' : 'Formatierung auswählen'
          }
          value={selectedTypesCount === 1 ? selected![0] : ''}
          onValueChange={(format: Block) => toggleBlock(editor, format)}
          className="text-left md:w-[150px]"
          onCloseAutoFocus={handleClose}
          Trigger={(props) => (
            <Trigger
              {...props}
              value={
                selectedTypesCount === 1 ? (selected![0] as Block) : 'paragraph'
              }
            />
          )}
        >
          {children}
        </SelectPrimitive>
      </div>
    </Tooltip>
  )
}

export function SelectItem({ format }: { format: Block }) {
  const title = LABEL_MAP[format]?.[1]

  return (
    <SelectItemPrimitve value={format}>
      <span className={styleMap[format]}>{title}</span>
    </SelectItemPrimitve>
  )
}

type ToolbarTriggerProps = TriggerProps & { value: Block }

function Trigger(props: ToolbarTriggerProps) {
  const isMobile = useMediaQuery('(max-width: 768px)')

  if (isMobile) {
    return (
      <SelectPrimitive.Trigger
        className={cx('button hover:bg-gray-20', props.className)}
        aria-label={props.label}
      >
        <SelectPrimitive.Icon className="h-4 w-4 text-black">
          <TextScale />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    )
  }

  return <ToolbarTrigger {...props} />
}

function ToolbarTrigger({
  className,
  label,
  placeholder,
  value,
}: ToolbarTriggerProps) {
  const title = LABEL_MAP[value]?.[1]

  return (
    <SelectPrimitive.Trigger
      className={cx('button hover:bg-gray-20', className)}
      aria-label={label}
    >
      <div className="line-clamp-1 h-4">{title || placeholder}</div>
      <SelectPrimitive.Icon className="h-4 w-4 text-black">
        <ChevronDown />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}
